import { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Callout } from '../../components/ui/Callout';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import { extractErrorMessage } from '../../api/errors';
import { useUpdateBrandSecret, type SecretSummary } from '../../queries/brandSettings';

const DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
const inputClass = 'w-full rounded-md border border-input bg-background px-2 py-2 font-mono text-sm';

interface SecretsPanelProps {
  secrets: SecretSummary[];
  enabled: boolean;
  canWrite: boolean;
  brandName: string;
}

/**
 * Write-only provider keys. A stored value is only ever shown as its last
 * four characters; entering a new value replaces it after the server has
 * validated it (shape + a live check with the provider where possible).
 */
export function SecretsPanel({ secrets, enabled, canWrite, brandName }: SecretsPanelProps) {
  const update = useUpdateBrandSecret();
  const toast = useToast();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<SecretSummary | null>(null);

  const submit = (secret: SecretSummary) => {
    const value = drafts[secret.name]?.trim();
    if (!value) return;
    update.mutate(
      { name: secret.name, value },
      {
        onSuccess: (result) => {
          setDrafts((prev) => ({ ...prev, [secret.name]: '' }));
          setConfirming(null);
          toast.success(`${secret.label} updated (…${result.last4}). Takes effect within ${result.propagationMinutes} minutes.`);
        },
        onError: (err) => {
          setConfirming(null);
          toast.error(extractErrorMessage(err, `Failed to update ${secret.label}.`));
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-4 w-4" /> Provider keys
        </CardTitle>
        <CardDescription>
          {brandName}'s Stripe and Brevo credentials. Values are write-only: only the last four characters are ever shown. A new key is
          verified with the provider before it is stored and takes effect within about five minutes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!enabled && (
          <Callout variant="warning" title="Secret management is disabled on this deployment">
            This backend runs with BRAND_SECRETS_SSM_DISABLED (the dev rig), so keys cannot be read or changed from here.
          </Callout>
        )}
        <ul className="divide-y divide-border">
          {secrets.map((secret) => (
            <li key={secret.name} className="grid grid-cols-1 gap-3 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{secret.label}</span>
                  {secret.configured ? (
                    <Badge variant="success">Configured ····{secret.last4}</Badge>
                  ) : (
                    <Badge variant="warning">Not set</Badge>
                  )}
                  {secret.envOverride && (
                    <Badge variant="destructive" title="A per-brand environment variable on this box outranks the stored value; an update here will not take effect until it is removed.">
                      Env override
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{secret.hint}</p>
                {secret.updatedAt && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Updated {DATE_FORMATTER.format(new Date(secret.updatedAt))}
                    {secret.updatedBy ? ` by ${secret.updatedBy}` : ''}
                  </p>
                )}
              </div>
              {canWrite && (
                <form
                  className="flex items-start gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (drafts[secret.name]?.trim()) setConfirming(secret);
                  }}
                >
                  <input
                    className={inputClass}
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={secret.configured ? 'Enter a new value to replace' : 'Enter value'}
                    aria-label={`New ${secret.label}`}
                    value={drafts[secret.name] ?? ''}
                    disabled={!enabled || update.isPending}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [secret.name]: e.target.value }))}
                  />
                  <Button type="submit" variant="outline" disabled={!enabled || update.isPending || !drafts[secret.name]?.trim()}>
                    {secret.configured ? 'Replace' : 'Set'}
                  </Button>
                </form>
              )}
            </li>
          ))}
        </ul>
      </CardContent>

      <ConfirmDialog
        open={confirming !== null}
        title={confirming ? `Replace ${confirming.label}?` : ''}
        description={
          confirming
            ? `The new ${confirming.label} will be verified with the provider, then replace the current one for ${brandName}${confirming.configured ? ` (····${confirming.last4})` : ''}. Live systems switch to it within about five minutes. Keep the old key valid until you have confirmed the new one works.`
            : ''
        }
        confirmLabel="Verify and replace"
        destructive
        busy={update.isPending}
        onConfirm={() => confirming && submit(confirming)}
        onOpenChange={(open) => !open && !update.isPending && setConfirming(null)}
      />
    </Card>
  );
}
